import React, { useEffect, useState, useContext } from "react";

import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";

import useSettings from "../../hooks/useSettings";
import { ToastContainer, toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import { Box, Typography } from "@material-ui/core";

import { Tab, Tabs, TextField, Paper } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import api from "../../services/api";
import SettingsIcon from "@material-ui/icons/Settings";
import AssignmentIcon from "@material-ui/icons/Assignment";
import TuneIcon from "@material-ui/icons/Tune";
import SecurityIcon from "@material-ui/icons/Security";
import PaymentIcon from "@material-ui/icons/Payment";
import ChatIcon from "@material-ui/icons/Chat";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(4),
    width: "100%",
  },
  sectionCard: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.92rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    borderBottom: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}26` : "1px solid rgba(0, 0, 0, 0.08)",
    paddingBottom: theme.spacing(1),
    "& svg": {
      marginRight: theme.spacing(1),
      fontSize: "1.25rem",
      color: theme.palette.primary.main,
    },
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#FFFFFF",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.85rem",
    },
  },
  paymentTabs: {
    marginBottom: theme.spacing(2.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#f5f5f5",
    "& .MuiTab-root": {
      fontSize: "0.82rem",
      fontWeight: 600,
      minHeight: 42,
      textTransform: "none",
    },
    "& .Mui-selected": {
      color: `${theme.palette.primary.main} !important`,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette.primary.main,
      height: 3,
      borderRadius: 2,
    },
  },
  messagePreview: {
    backgroundColor: theme.mode === "light" ? theme.palette.grey[100] : theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.mode === "light" ? theme.palette.grey[300] : theme.palette.divider}`
  },
  variableChip: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
    fontFamily: "monospace",
    margin: "0 2px"
  },
  helperUpdating: {
    color: `${theme.palette.primary.main} !important`,
    fontWeight: 600,
    fontSize: "0.72rem",
  },
}));

const getTimeZones = () => {
  try {
    const timeZones = Intl.supportedValuesOf('timeZone');
    const now = new Date();

    return timeZones.map(tz => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'shortOffset',
        });
        const parts = formatter.formatToParts(now);
        const offset = parts.find(part => part.type === 'timeZoneName').value;

        return {
          name: tz,
          label: `(${offset}) ${tz}`,
        };
      } catch (e) {
        return { name: tz, label: tz };
      }
    }).sort((a, b) => {
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });
  } catch (e) {
    // Fallback si Intl.supportedValuesOf no está disponible
    return [
      { name: "America/Lima", label: "(GMT-05:00) America/Lima" },
      { name: "America/Sao_Paulo", label: "(GMT-03:00) America/Sao_Paulo" },
      { name: "America/Mexico_City", label: "(GMT-06:00) America/Mexico_City" },
      { name: "America/Bogota", label: "(GMT-05:00) America/Bogota" },
      { name: "America/Santiago", label: "(GMT-04:00) America/Santiago" },
      { name: "America/Argentina/Buenos_Aires", label: "(GMT-03:00) America/Argentina/Buenos_Aires" },
      { name: "America/Caracas", label: "(GMT-04:00) America/Caracas" },
      { name: "America/Guayaquil", label: "(GMT-05:00) America/Guayaquil" },
      { name: "America/La_Paz", label: "(GMT-04:00) America/La_Paz" },
      { name: "UTC", label: "(GMT) UTC" }
    ];
  }
};

const tzList = getTimeZones();

export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user, company } = props;

  const classes = useStyles();
  const [paymentTab, setPaymentTab] = useState("efi");
  const [resellerPaymentLink, setResellerPaymentLink] = useState("");
  const [loadingResellerPaymentLink, setLoadingResellerPaymentLink] = useState(false);
  const [userRating, setUserRating] = useState("disabled");
  const [scheduleType, setScheduleType] = useState("disabled");
  const [chatBotType, setChatBotType] = useState("text");

  const [loadingUserRating, setLoadingUserRating] = useState(false);
  const [loadingScheduleType, setLoadingScheduleType] = useState(false);

  const [userCreation, setUserCreation] = useState("disabled");
  const [loadingUserCreation, setLoadingUserCreation] = useState(false);

  const [SendGreetingAccepted, setSendGreetingAccepted] = useState("enabled");
  const [loadingSendGreetingAccepted, setLoadingSendGreetingAccepted] = useState(false);

  const [UserRandom, setUserRandom] = useState("enabled");
  const [loadingUserRandom, setLoadingUserRandom] = useState(false);

  const [SettingsTransfTicket, setSettingsTransfTicket] = useState("enabled");
  const [loadingSettingsTransfTicket, setLoadingSettingsTransfTicket] = useState(false);

  const [sendSignMessage, setSendSignMessage] = useState("enabled");
  const [loadingSendSignMessage, setLoadingSendSignMessage] = useState(false);

  const [sendGreetingMessageOneQueues, setSendGreetingMessageOneQueues] = useState("enabled");
  const [loadingSendGreetingMessageOneQueues, setLoadingSendGreetingMessageOneQueues] = useState(false);

  const [sendQueuePosition, setSendQueuePosition] = useState("enabled");
  const [loadingSendQueuePosition, setLoadingSendQueuePosition] = useState(false);

  const [sendFarewellWaitingTicket, setSendFarewellWaitingTicket] = useState("enabled");
  const [loadingSendFarewellWaitingTicket, setLoadingSendFarewellWaitingTicket] = useState(false);

  const [acceptAudioMessageContact, setAcceptAudioMessageContact] = useState("enabled");
  const [loadingAcceptAudioMessageContact, setLoadingAcceptAudioMessageContact] = useState(false);

  //PAYMENT METHODS
  const [eficlientidType, setEfiClientidType] = useState('');
  const [loadingEfiClientidType, setLoadingEfiClientidType] = useState(false);

  const [eficlientsecretType, setEfiClientsecretType] = useState('');
  const [loadingEfiClientsecretType, setLoadingEfiClientsecretType] =
    useState(false);

  const [efichavepixType, setEfiChavepixType] = useState('');
  const [loadingEfiChavepixType, setLoadingEfiChavepixType] = useState(false);

  const [mpaccesstokenType, setmpaccesstokenType] = useState('');
  const [loadingmpaccesstokenType, setLoadingmpaccesstokenType] =
    useState(false);

  const [stripeprivatekeyType, setstripeprivatekeyType] = useState('');
  const [loadingstripeprivatekeyType, setLoadingstripeprivatekeyType] =
    useState(false);

  const [asaastokenType, setasaastokenType] = useState('');
  const [loadingasaastokenType, setLoadingasaastokenType] = useState(false);

  //OPENAI API KEY TRANSCRIÇÃO DE ÁUDIO
  const [openaitokenType, setopenaitokenType] = useState('');
  const [loadingopenaitokenType, setLoadingopenaitokenType] = useState(false);

  //LGPD
  const [enableLGPD, setEnableLGPD] = useState("disabled");
  const [loadingEnableLGPD, setLoadingEnableLGPD] = useState(false);

  const [lgpdMessage, setLGPDMessage] = useState("");
  const [loadinglgpdMessage, setLoadingLGPDMessage] = useState(false);

  const [lgpdLink, setLGPDLink] = useState("");
  const [loadingLGPDLink, setLoadingLGPDLink] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState("disabled");
  const [loadingLGPDDeleteMessage, setLoadingLGPDDeleteMessage] = useState(false);

  //LIMITAR DOWNLOAD
  // const [downloadLimit, setdownloadLimit] = useState("64");
  // const [loadingDownloadLimit, setLoadingdownloadLimit] = useState(false);

  const [lgpdConsent, setLGPDConsent] = useState("disabled");
  const [loadingLGPDConsent, setLoadingLGPDConsent] = useState(false);

  const [lgpdHideNumber, setLGPDHideNumber] = useState("disabled");
  const [loadingLGPDHideNumber, setLoadingLGPDHideNumber] = useState(false);

  // Tag obrigatoria
  const [requiredTag, setRequiredTag] = useState("enabled")
  const [loadingRequiredTag, setLoadingRequiredTag] = useState(false)

  // Fechar ticket ao transferir para outro setor
  const [closeTicketOnTransfer, setCloseTicketOnTransfer] = useState(false)
  const [loadingCloseTicketOnTransfer, setLoadingCloseTicketOnTransfer] = useState(false)

  // Status do ticket ao transferir com usuário
  const [transferredTicketStatus, setTransferredTicketStatus] = useState("open")
  const [loadingTransferredTicketStatus, setLoadingTransferredTicketStatus] = useState(false)

  // Usar carteira de clientes
  const [directTicketsToWallets, setDirectTicketsToWallets] = useState(false)
  const [loadingDirectTicketsToWallets, setLoadingDirectTicketsToWallets] = useState(false)

  // Sigla para inserir no copiar contatos
  const [copyContactPrefix, setCopyContactPrefix] = useState("");
  const [loadingCopyContactPrefix, setLoadingCopyContactPrefix] = useState(false);

  //MENSAGENS CUSTOMIZADAS
  const [transferMessage, setTransferMessage] = useState("");
  const [loadingTransferMessage, setLoadingTransferMessage] = useState(false);

  const [greetingAcceptedMessage, setGreetingAcceptedMessage] = useState("");
  const [loadingGreetingAcceptedMessage, setLoadingGreetingAcceptedMessage] = useState(false);

  const [sendQueuePositionMessage, setSendQueuePositionMessage] = useState("");
  const [loadingSendQueuePositionMessage, setLoadingSendQueuePositionMessage] = useState(false);

  const [showNotificationPending, setShowNotificationPending] = useState(false);
  const [loadingShowNotificationPending, setLoadingShowNotificationPending] = useState(false);

  // SYSTEM CURRENCY (SUPERADMIN)
  const [systemCurrency, setSystemCurrency] = useState("USD $");
  const [loadingSystemCurrency, setLoadingSystemCurrency] = useState(false);

  // SYSTEM LANGUAGE (SUPERADMIN)
  const [systemLanguage, setSystemLanguage] = useState("es");
  const [loadingSystemLanguage, setLoadingSystemLanguage] = useState(false);

  const [timezone, setTimezone] = useState("America/Lima");
  const [loadingTimezone, setLoadingTimezone] = useState(false);

  const { update: updateUserCreation, getAll } = useSettings();

  // const { update: updatedownloadLimit } = useSettings();

  const { update: updateeficlientid } = useSettings();
  const { update: updateeficlientsecret } = useSettings();
  const { update: updateefichavepix } = useSettings();
  const { update: updatempaccesstoken } = useSettings();
  const { update: updatestripeprivatekey } = useSettings();
  const { update: updateasaastoken } = useSettings();

  const { update } = useCompanySettings();

  const isSuper = () => {
    return user.super || user.profile === "admin";
  };


  useEffect(() => {
    if (company && company.resellerPaymentLink !== undefined) {
      setResellerPaymentLink(company.resellerPaymentLink || "");
    }
  }, [company]);

  useEffect(() => {

    if (Array.isArray(oldSettings) && oldSettings.length) {

      const userPar = oldSettings.find((s) => s.key === "userCreation");

      if (userPar) {
        setUserCreation(userPar.value);
      }

      // const downloadLimit = oldSettings.find((s) => s.key === "downloadLimit");

      // if (downloadLimit) {
      //  setdownloadLimit(downloadLimit.value);
      // }

      const copyContactPrefix = oldSettings.find((s) => s.key === 'copyContactPrefix');
      if (copyContactPrefix) {
        setCopyContactPrefix(copyContactPrefix.value);
      }

      const eficlientidType = oldSettings.find((s) => s.key === 'eficlientid');
      if (eficlientidType) {
        setEfiClientidType(eficlientidType.value);
      }

      const eficlientsecretType = oldSettings.find((s) => s.key === 'eficlientsecret');
      if (eficlientsecretType) {
        setEfiClientsecretType(eficlientsecretType.value);
      }

      const efichavepixType = oldSettings.find((s) => s.key === 'efichavepix');
      if (efichavepixType) {
        setEfiChavepixType(efichavepixType.value);
      }

      const mpaccesstokenType = oldSettings.find((s) => s.key === 'mpaccesstoken');
      if (mpaccesstokenType) {
        setmpaccesstokenType(mpaccesstokenType.value);
      }

      const asaastokenType = oldSettings.find((s) => s.key === 'asaastoken');
      if (asaastokenType) {
        setasaastokenType(asaastokenType.value);
      }

      const systemCurrencyValue = oldSettings.find((s) => s.key === 'system_currency');
      if (systemCurrencyValue) {
        setSystemCurrency(systemCurrencyValue.value);
      }

      const systemLanguageValue = oldSettings.find((s) => s.key === 'system_language');
      if (systemLanguageValue) {
        setSystemLanguage(systemLanguageValue.value);
      }

    }
  }, [oldSettings])




  useEffect(() => {




    for (const [key, value] of Object.entries(settings)) {
      if (key === "userRating") setUserRating(value);
      if (key === "scheduleType") setScheduleType(value);
      if (key === "chatBotType") setChatBotType(value);
      if (key === "userRandom") setUserRandom(value);
      if (key === "sendGreetingMessageOneQueues") setSendGreetingMessageOneQueues(value);
      if (key === "sendSignMessage") setSendSignMessage(value);
      if (key === "sendFarewellWaitingTicket") setSendFarewellWaitingTicket(value);
      if (key === "sendGreetingAccepted") setSendGreetingAccepted(value);
      if (key === "sendQueuePosition") setSendQueuePosition(value);
      if (key === "acceptAudioMessageContact") setAcceptAudioMessageContact(value);
      if (key === "enableLGPD") setEnableLGPD(value);
      if (key === "requiredTag") setRequiredTag(value);
      if (key === "lgpdDeleteMessage") setLGPDDeleteMessage(value)
      if (key === "lgpdHideNumber") setLGPDHideNumber(value);
      if (key === "lgpdConsent") setLGPDConsent(value);
      if (key === "lgpdMessage") setLGPDMessage(value);
      if (key === "sendMsgTransfTicket") setSettingsTransfTicket(value);
      if (key === "lgpdLink") setLGPDLink(value);
      if (key === "DirectTicketsToWallets") setDirectTicketsToWallets(value);
      if (key === "closeTicketOnTransfer") setCloseTicketOnTransfer(value);
      if (key === "transferredTicketStatus") setTransferredTicketStatus(value);

      if (key === "showNotificationPending") setShowNotificationPending(value);
      if (key === "copyContactPrefix") setCopyContactPrefix(value);

      if (key === "system_currency") setSystemCurrency(value);
      if (key === "system_language") setSystemLanguage(value);

      // DIRECT LOAD: NO CLEANING, NO FILTERING.
      // Prioritizing persistence over translation features.
      if (key === "transferMessage") setTransferMessage(value);
      if (key === "greetingAcceptedMessage") setGreetingAcceptedMessage(value);
      if (key === "sendQueuePositionMessage") setSendQueuePositionMessage(value);
      if (key === "timezone") {
        console.log(`[TIMEZONE-DEBUG] Options: Found timezone in settings: ${value}`);
        setTimezone(value);
      }
    }



    // Force enable on load
    if (settings.sendMsgTransfTicket === "disabled") handleSettingsTransfTicket("enabled");
    if (settings.sendGreetingAccepted === "disabled") handleSendGreetingAccepted("enabled");
    if (settings.sendQueuePosition === "disabled") handleSendQueuePosition("enabled");

  }, [settings]);

  async function handleChangeUserCreation(value) {
    setUserCreation(value);
    setLoadingUserCreation(true);
    await updateUserCreation({
      key: "userCreation",
      value,
    });
    setLoadingUserCreation(false);
  }

  // async function handleDownloadLimit(value) {
  //   setdownloadLimit(value);
  //   setLoadingdownloadLimit(true);
  //   await updatedownloadLimit({
  //     key: "downloadLimit",
  //     value,
  //   });
  //   setLoadingdownloadLimit(false);
  // }

  async function handleChangeEfiClientid(value) {
    setEfiClientidType(value);
    setLoadingEfiClientidType(true);
    await updateeficlientid({
      key: 'eficlientid',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingEfiClientidType(false);
  }

  async function handleCopyContactPrefix(value) {
    setCopyContactPrefix(value);
    setLoadingCopyContactPrefix(true);
    await update({
      column: "copyContactPrefix",
      data: value
    });
    setLoadingCopyContactPrefix(false);
  }

  async function handleChangeEfiClientsecret(value) {
    setEfiClientsecretType(value);
    setLoadingEfiClientsecretType(true);
    await updateeficlientsecret({
      key: 'eficlientsecret',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingEfiClientsecretType(false);
  }

  async function handleChangeEfiChavepix(value) {
    setEfiChavepixType(value);
    setLoadingEfiChavepixType(true);
    await updateefichavepix({
      key: 'efichavepix',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingEfiChavepixType(false);
  }

  async function handleChangempaccesstoken(value) {
    setmpaccesstokenType(value);
    setLoadingmpaccesstokenType(true);
    await updatempaccesstoken({
      key: 'mpaccesstoken',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingmpaccesstokenType(false);
  }

  async function handleChangestripeprivatekey(value) {
    setstripeprivatekeyType(value);
    setLoadingstripeprivatekeyType(true);
    await updatestripeprivatekey({
      key: 'stripeprivatekey',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingstripeprivatekeyType(false);
  }

  async function handleChangeasaastoken(value) {
    setasaastokenType(value);
    setLoadingasaastokenType(true);
    await updateasaastoken({
      key: 'asaastoken',
      value,
    });
    toast.success(i18n.t("settings.success.updated"));
    setLoadingasaastokenType(false);
  }

  async function handleChangeUserRating(value) {
    setUserRating(value);
    setLoadingUserRating(true);
    await update({
      column: "userRating",
      data: value
    });
    setLoadingUserRating(false);
  }

  async function handleScheduleType(value) {
    setScheduleType(value);
    setLoadingScheduleType(true);
    await update({
      column: "scheduleType",
      data: value
    });
    setLoadingScheduleType(false);
    if (typeof scheduleTypeChanged === "function") {
      scheduleTypeChanged(value);
    }
  }

  async function handleCopyContactPrefix(value) {
    setCopyContactPrefix(value);
    setLoadingCopyContactPrefix(true);
    await update({
      column: "copyContactPrefix",
      data: value
    });
    setLoadingCopyContactPrefix(false);
  }

  async function handleChatBotType(value) {
    setChatBotType(value);
    await update({
      column: "chatBotType",
      data: value
    });
    if (typeof scheduleTypeChanged === "function") {
      setChatBotType(value);
    }
  }

  async function handleLGPDMessage(value) {
    setLGPDMessage(value);
    setLoadingLGPDMessage(true);
    await update({
      column: "lgpdMessage",
      data: value
    });
    setLoadingLGPDMessage(false);
  }

  async function handletransferMessage(value) {
    setLoadingTransferMessage(true);
    try {
      await update({
        column: "transferMessage",
        data: value
      });
      toast.success(i18n.t("settings.settings.options.updating"));
    } catch (err) {
      toast.error(err);
    }
    setLoadingTransferMessage(false);
  }

  async function handleGreetingAcceptedMessage(value) {
    setLoadingGreetingAcceptedMessage(true);
    try {
      await update({
        column: "greetingAcceptedMessage",
        data: value
      });
      toast.success(i18n.t("settings.settings.options.updating"));
    } catch (err) {
      toast.error(err);
    }
    setLoadingGreetingAcceptedMessage(false);
  }

  async function handlesendQueuePositionMessage(value) {
    setLoadingSendQueuePositionMessage(true);
    try {
      await update({
        column: "sendQueuePositionMessage",
        data: value
      });
      toast.success(i18n.t("settings.settings.options.updating"));
    } catch (err) {
      toast.error(err);
    }
    setLoadingSendQueuePositionMessage(false);
  }

  async function handleShowNotificationPending(value) {
    setShowNotificationPending(value);
    setLoadingShowNotificationPending(true);
    await update({
      column: "showNotificationPending",
      data: value
    });
    setLoadingShowNotificationPending(false);
  }

  async function handleLGPDLink(value) {
    setLGPDLink(value);
    setLoadingLGPDLink(true);
    await update({
      column: "lgpdLink",
      data: value
    });
    setLoadingLGPDLink(false);
  }

  async function handleLGPDDeleteMessage(value) {
    setLGPDDeleteMessage(value);
    setLoadingLGPDDeleteMessage(true);
    await update({
      column: "lgpdDeleteMessage",
      data: value
    });
    setLoadingLGPDDeleteMessage(false);
  }

  async function handleLGPDConsent(value) {
    setLGPDConsent(value);
    setLoadingLGPDConsent(true);
    await update({
      column: "lgpdConsent",
      data: value
    });
    setLoadingLGPDConsent(false);
  }

  async function handleLGPDHideNumber(value) {
    setLGPDHideNumber(value);
    setLoadingLGPDHideNumber(true);
    await update({
      column: "lgpdHideNumber",
      data: value
    });
    setLoadingLGPDHideNumber(false);
  }

  async function handleSendGreetingAccepted(value) {
    setSendGreetingAccepted(value);
    setLoadingSendGreetingAccepted(true);
    await update({
      column: "sendGreetingAccepted",
      data: value
    });
    setLoadingSendGreetingAccepted(false);
  }

  async function handleUserRandom(value) {
    setUserRandom(value);
    setLoadingUserRandom(true);
    await update({
      column: "userRandom",
      data: value
    });
    setLoadingUserRandom(false);
  }

  async function handleSettingsTransfTicket(value) {
    setSettingsTransfTicket(value);
    setLoadingSettingsTransfTicket(true);
    await update({
      column: "sendMsgTransfTicket",
      data: value
    });
    setLoadingSettingsTransfTicket(false);
  }

  async function handleSendSignMessage(value) {
    setSendSignMessage(value);
    setLoadingSendSignMessage(true);
    await update({
      column: "sendSignMessage",
      data: value
    });
    localStorage.setItem("sendSignMessage", value === "enabled" ? true : false); //atualiza localstorage para sessão
    setLoadingSendSignMessage(false);
  }

  async function handleSendGreetingMessageOneQueues(value) {
    setSendGreetingMessageOneQueues(value);
    setLoadingSendGreetingMessageOneQueues(true);
    await update({
      column: "sendGreetingMessageOneQueues",
      data: value
    });
    setLoadingSendGreetingMessageOneQueues(false);
  }

  async function handleSendQueuePosition(value) {
    setSendQueuePosition(value);
    setLoadingSendQueuePosition(true);
    await update({
      column: "sendQueuePosition",
      data: value
    });
    setLoadingSendQueuePosition(false);
  }

  async function handleSendFarewellWaitingTicket(value) {
    setSendFarewellWaitingTicket(value);
    setLoadingSendFarewellWaitingTicket(true);
    await update({
      column: "sendFarewellWaitingTicket",
      data: value
    });
    setLoadingSendFarewellWaitingTicket(false);
  }

  async function handleAcceptAudioMessageContact(value) {
    setAcceptAudioMessageContact(value);
    setLoadingAcceptAudioMessageContact(true);
    await update({
      column: "acceptAudioMessageContact",
      data: value
    });
    setLoadingAcceptAudioMessageContact(false);
  }

  async function handleEnableLGPD(value) {
    setEnableLGPD(value);
    setLoadingEnableLGPD(true);
    await update({
      column: "enableLGPD",
      data: value
    });
    setLoadingEnableLGPD(false);
  }

  async function handleRequiredTag(value) {
    setRequiredTag(value);
    setLoadingRequiredTag(true);
    await update({
      column: "requiredTag",
      data: value,
    });
    setLoadingRequiredTag(false);
  }

  async function handleCloseTicketOnTransfer(value) {
    setCloseTicketOnTransfer(value);
    setLoadingCloseTicketOnTransfer(true);
    await update({
      column: "closeTicketOnTransfer",
      data: value,
    });
    setLoadingCloseTicketOnTransfer(false);
  }

  async function handleTransferredTicketStatus(value) {
    setTransferredTicketStatus(value);
    setLoadingTransferredTicketStatus(true);
    await update({
      column: "transferredTicketStatus",
      data: value,
    });
    setLoadingTransferredTicketStatus(false);
  }

  async function handleDirectTicketsToWallets(value) {
    setDirectTicketsToWallets(value);
    setLoadingDirectTicketsToWallets(true);
    await update({
      column: "DirectTicketsToWallets",
      data: value,
    });
    setLoadingDirectTicketsToWallets(false);
  }

  async function handleTimezoneChange(value) {
    setTimezone(value);
    setLoadingTimezone(true);
    await update({
      column: "timezone",
      data: value,
    });
    setLoadingTimezone(false);
  }

  const renderMessagePreview = (message) => {
    if (!message) return null;

    let previewMessage = message;

    // Simulate variables
    // For Greeting: {name} (if used), {user} etc - adding simplistic ones for now if applicable, mostly Queue uses {position}
    previewMessage = previewMessage.replace(
      /{{position}}/g,
      `<span class="${classes.variableChip}">1</span>`
    ).replace(
      /{position}/g,
      `<span class="${classes.variableChip}">1</span>`
    ).replace(
      /{nome}/g,
      `<span class="${classes.variableChip}">Client Name</span>`
    ).replace(
      /{name}/g,
      `<span class="${classes.variableChip}">Client Name</span>`
    );

    return (
      <Box className={classes.messagePreview}>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("birthdaySettings.info.preview") || "Preview:"}
        </Typography>
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{ __html: previewMessage }}
        />
      </Box>
    );
  };

  return (
    <div className={classes.container}>
      {/* SECCIÓN 1: PARÁMETROS GLOBALES DEL SISTEMA */}
      <Paper className={classes.sectionCard} elevation={0}>
        <Typography className={classes.sectionHeader}>
          <SettingsIcon />
          {i18n.t("settings.settings.sections.globalParams") || "Parámetros Globales del Sistema"}
        </Typography>
        <Grid container spacing={2}>
          {isSuper() && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                <InputLabel id="UserCreation-label">
                  {i18n.t("settings.settings.options.creationCompanyUser")}
                </InputLabel>
                <Select
                  labelId="UserCreation-label"
                  label={i18n.t("settings.settings.options.creationCompanyUser")}
                  value={userCreation}
                  onChange={async (e) => {
                    handleChangeUserCreation(e.target.value);
                  }}
                >
                  <MenuItem value={"disabled"}>
                    {i18n.t("settings.settings.options.disabled")}
                  </MenuItem>
                  <MenuItem value={"enabled"}>
                    {i18n.t("settings.settings.options.enabled")}
                  </MenuItem>
                </Select>
                {loadingUserCreation && (
                  <FormHelperText className={classes.helperUpdating}>
                    {i18n.t("settings.settings.options.updating")}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          {isSuper() && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                <TextField
                  id="system-currency"
                  label={i18n.t("settings.settings.options.systemCurrency")}
                  variant="outlined"
                  size="small"
                  value={systemCurrency}
                  onChange={async (e) => {
                    setSystemCurrency(e.target.value);
                  }}
                  onBlur={async (e) => {
                    setLoadingSystemCurrency(true);
                    await updateUserCreation({
                      key: "system_currency",
                      value: e.target.value
                    });
                    toast.success(i18n.t("settings.success.updated"));
                    setLoadingSystemCurrency(false);
                  }}
                  fullWidth
                />
              </FormControl>
            </Grid>
          )}

          {isSuper() && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                <InputLabel id="system-language-label">
                  {i18n.t("settings.settings.options.systemLanguageLabel")}
                </InputLabel>
                <Select
                  labelId="system-language-label"
                  label={i18n.t("settings.settings.options.systemLanguageLabel")}
                  value={systemLanguage}
                  onChange={async (e) => {
                    setSystemLanguage(e.target.value);
                    setLoadingSystemLanguage(true);
                    await updateUserCreation({
                      key: "system_language",
                      value: e.target.value
                    });
                    toast.success(i18n.t("settings.success.updated"));
                    setLoadingSystemLanguage(false);
                  }}
                >
                  <MenuItem value={"es"}>Español</MenuItem>
                  <MenuItem value={"pt"}>Português</MenuItem>
                  <MenuItem value={"en"}>English</MenuItem>
                  <MenuItem value={"fr"}>Français</MenuItem>
                  <MenuItem value={"ar"}>عربي</MenuItem>
                </Select>
                {loadingSystemLanguage && (
                  <FormHelperText className={classes.helperUpdating}>
                    {i18n.t("settings.settings.options.updating")}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="timezone-label">
                {i18n.t("settings.settings.options.timezone")}
              </InputLabel>
              <Select
                labelId="timezone-label"
                label={i18n.t("settings.settings.options.timezone")}
                value={timezone}
                onChange={async (e) => {
                  handleTimezoneChange(e.target.value);
                }}
              >
                {tzList.map((tz) => (
                  <MenuItem key={tz.name} value={tz.name}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
              {loadingTimezone && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <TextField
                id="resellerPaymentLink"
                name="resellerPaymentLink"
                size="small"
                label={i18n.t("settings.settings.sections.resellerPaymentLink") || "Enlace de Pago Externo (Reseller)"}
                variant="outlined"
                value={resellerPaymentLink}
                onChange={async (e) => {
                  setResellerPaymentLink(e.target.value);
                }}
                onBlur={async () => {
                  setLoadingResellerPaymentLink(true);
                  try {
                    await api.put(`/companies/${company.id}`, { resellerPaymentLink });
                    toast.success(i18n.t("settings.success.updated"));
                  } catch (err) {
                    toast.error(err);
                  }
                  setLoadingResellerPaymentLink(false);
                }}
                helperText={loadingResellerPaymentLink ? i18n.t("settings.settings.options.updating") : (i18n.t("settings.settings.sections.resellerPaymentLinkHelp") || "Tus clientes irán a este link para pagar su plan.")}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <TextField
                id="copyContactPrefix"
                name="copyContactPrefix"
                size="small"
                label={i18n.t("settings.settings.options.copyContactPrefix")}
                variant="outlined"
                value={copyContactPrefix}
                placeholder={i18n.t("settings.settings.options.copyContactPrefixPlaceholder")}
                onChange={async (e) => {
                  handleCopyContactPrefix(e.target.value);
                }}
                helperText={loadingCopyContactPrefix && i18n.t("settings.settings.options.updating")}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SECCIÓN 2: ATENCIÓN Y ENRUTAMIENTO DE TICKETS */}
      <Paper className={classes.sectionCard} elevation={0}>
        <Typography className={classes.sectionHeader}>
          <AssignmentIcon />
          {i18n.t("settings.settings.sections.ticketRouting") || "Atención y Enrutamiento de Tickets"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="schedule-type-label">
                {i18n.t("settings.settings.options.officeScheduling")}
              </InputLabel>
              <Select
                labelId="schedule-type-label"
                label={i18n.t("settings.settings.options.officeScheduling")}
                value={scheduleType}
                onChange={async (e) => {
                  handleScheduleType(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={"queue"}>{i18n.t("settings.settings.options.queueManagement")}</MenuItem>
                <MenuItem value={"company"}>{i18n.t("settings.settings.options.companyManagement")}</MenuItem>
                <MenuItem value={"connection"}>{i18n.t("settings.settings.options.connectionManagement")}</MenuItem>
              </Select>
              {loadingScheduleType && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="userRandom-label">
                {i18n.t("settings.settings.options.userRandom")}
              </InputLabel>
              <Select
                labelId="userRandom-label"
                label={i18n.t("settings.settings.options.userRandom")}
                value={UserRandom}
                onChange={async (e) => {
                  handleUserRandom(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingUserRandom && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="ratings-label">{i18n.t("settings.settings.options.evaluations")}</InputLabel>
              <Select
                labelId="ratings-label"
                label={i18n.t("settings.settings.options.evaluations")}
                value={userRating}
                onChange={async (e) => {
                  handleChangeUserRating(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingUserRating && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="sendGreetingMessageOneQueues-label">
                {i18n.t("settings.settings.options.sendGreetingMessageOneQueues")}
              </InputLabel>
              <Select
                labelId="sendGreetingMessageOneQueues-label"
                label={i18n.t("settings.settings.options.sendGreetingMessageOneQueues")}
                value={sendGreetingMessageOneQueues}
                onChange={async (e) => {
                  handleSendGreetingMessageOneQueues(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingSendGreetingMessageOneQueues && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="sendFarewellWaitingTicket-label">
                {i18n.t("settings.settings.options.sendFarewellWaitingTicket")}
              </InputLabel>
              <Select
                labelId="sendFarewellWaitingTicket-label"
                label={i18n.t("settings.settings.options.sendFarewellWaitingTicket")}
                value={sendFarewellWaitingTicket}
                onChange={async (e) => {
                  handleSendFarewellWaitingTicket(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingSendFarewellWaitingTicket && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="closeTicketOnTransfer-label">
                {i18n.t("settings.settings.options.closeTicketOnTransfer")}
              </InputLabel>
              <Select
                labelId="closeTicketOnTransfer-label"
                label={i18n.t("settings.settings.options.closeTicketOnTransfer")}
                value={closeTicketOnTransfer}
                onChange={async (e) => {
                  handleCloseTicketOnTransfer(e.target.value);
                }}
              >
                <MenuItem value={false}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={true}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
              </Select>
              {loadingCloseTicketOnTransfer && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="transferredTicketStatus-label">
                {i18n.t("settings.settings.options.transferredTicketStatus")}
              </InputLabel>
              <Select
                labelId="transferredTicketStatus-label"
                label={i18n.t("settings.settings.options.transferredTicketStatus")}
                value={transferredTicketStatus}
                onChange={async (e) => {
                  handleTransferredTicketStatus(e.target.value);
                }}
              >
                <MenuItem value={"open"}>{i18n.t("settings.settings.options.open")}</MenuItem>
                <MenuItem value={"pending"}>{i18n.t("settings.settings.options.pending")}</MenuItem>
              </Select>
              {loadingTransferredTicketStatus && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="showNotificationPending-label">
                {i18n.t("settings.settings.options.showNotificationPending")}
              </InputLabel>
              <Select
                labelId="showNotificationPending-label"
                label={i18n.t("settings.settings.options.showNotificationPending")}
                value={showNotificationPending}
                onChange={async (e) => {
                  handleShowNotificationPending(e.target.value);
                }}
              >
                <MenuItem value={false}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={true}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
              </Select>
              {loadingShowNotificationPending && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="DirectTicketsToWallets-label">
                {i18n.t("settings.settings.options.DirectTicketsToWallets")}
              </InputLabel>
              <Select
                labelId="DirectTicketsToWallets-label"
                label={i18n.t("settings.settings.options.DirectTicketsToWallets")}
                value={directTicketsToWallets}
                onChange={async (e) => {
                  handleDirectTicketsToWallets(e.target.value);
                }}
              >
                <MenuItem value={false}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={true}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
              </Select>
              {loadingDirectTicketsToWallets && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SECCIÓN 3: COMPORTAMIENTO DE BOTS Y MENSAJERÍA */}
      <Paper className={classes.sectionCard} elevation={0}>
        <Typography className={classes.sectionHeader}>
          <TuneIcon />
          {i18n.t("settings.settings.sections.botMessaging") || "Comportamiento de Bots y Mensajería"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="chatbot-type-label">{i18n.t("settings.settings.options.chatBotType")}</InputLabel>
              <Select
                labelId="chatbot-type-label"
                label={i18n.t("settings.settings.options.chatBotType")}
                value={chatBotType}
                onChange={async (e) => {
                  handleChatBotType(e.target.value);
                }}
              >
                <MenuItem value={"text"}>{i18n.t("settings.settings.options.chatBotTypeText")}</MenuItem>
              </Select>
              {loadingScheduleType && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="sendSignMessage-label">
                {i18n.t("settings.settings.options.sendSignMessage")}
              </InputLabel>
              <Select
                labelId="sendSignMessage-label"
                label={i18n.t("settings.settings.options.sendSignMessage")}
                value={sendSignMessage}
                onChange={async (e) => {
                  handleSendSignMessage(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingSendSignMessage && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="acceptAudioMessageContact-label">
                {i18n.t("settings.settings.options.acceptAudioMessageContact")}
              </InputLabel>
              <Select
                labelId="acceptAudioMessageContact-label"
                label={i18n.t("settings.settings.options.acceptAudioMessageContact")}
                value={acceptAudioMessageContact}
                onChange={async (e) => {
                  handleAcceptAudioMessageContact(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>
                  {i18n.t("settings.settings.options.disabled")}
                </MenuItem>
                <MenuItem value={"enabled"}>
                  {i18n.t("settings.settings.options.enabled")}
                </MenuItem>
              </Select>
              {loadingAcceptAudioMessageContact && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* MENSAJES PERSONALIZADOS */}
          <Grid item xs={12} sm={6} md={6}>
            <FormControl className={classes.selectContainer}>
              <TextField
                id="greetingAcceptedMessage"
                name="greetingAcceptedMessage"
                label={i18n.t("settings.settings.customMessages.greetingAcceptedMessage")}
                variant="outlined"
                size="small"
                multiline
                rows={3}
                fullWidth
                value={greetingAcceptedMessage}
                placeholder={i18n.t("messagesList.header.buttons.greetingAcceptedDefault")}
                onChange={(e) => {
                  setGreetingAcceptedMessage(e.target.value);
                }}
                onBlur={async (e) => {
                  handleGreetingAcceptedMessage(e.target.value);
                }}
                helperText={loadingGreetingAcceptedMessage && i18n.t("settings.settings.options.updating")}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <FormControl className={classes.selectContainer}>
              <TextField
                id="transferMessage"
                name="transferMessage"
                multiline
                rows={3}
                size="small"
                label={i18n.t("settings.settings.customMessages.transferMessage")}
                placeholder={i18n.t("settings.settings.customMessages.transferMessageDefaultPlaceholder")}
                variant="outlined"
                value={transferMessage}
                onChange={(e) => {
                  setTransferMessage(e.target.value);
                }}
                onBlur={async (e) => {
                  handletransferMessage(e.target.value);
                }}
                helperText={loadingTransferMessage && i18n.t("settings.settings.options.updating")}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <FormControl className={classes.selectContainer}>
              <TextField
                id="sendQueuePositionMessage"
                name="sendQueuePositionMessage"
                multiline
                rows={3}
                size="small"
                label={i18n.t("settings.settings.customMessages.sendQueuePositionMessage")}
                placeholder={i18n.t("settings.settings.customMessages.sendQueuePositionMessageDefaultPlaceholder", { position: 1 })}
                variant="outlined"
                value={sendQueuePositionMessage}
                onChange={(e) => {
                  setSendQueuePositionMessage(e.target.value);
                }}
                onBlur={async (e) => {
                  handlesendQueuePositionMessage(e.target.value);
                }}
                helperText={loadingSendQueuePositionMessage && i18n.t("settings.settings.options.updating")}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SECCIÓN 4: SEGURIDAD, AUDITORÍA Y PRIVACIDAD (LGPD) */}
      <Paper className={classes.sectionCard} elevation={0}>
        <Typography className={classes.sectionHeader}>
          <SecurityIcon />
          {i18n.t("settings.settings.sections.securityLgpd") || "Seguridad, Auditoría y Privacidad (LGPD)"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="enableLGPD-label">{i18n.t("settings.settings.options.enableLGPD")}</InputLabel>
              <Select
                labelId="enableLGPD-label"
                label={i18n.t("settings.settings.options.enableLGPD")}
                value={enableLGPD}
                onChange={async (e) => {
                  handleEnableLGPD(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={"enabled"}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
              </Select>
              {loadingEnableLGPD && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" className={classes.selectContainer}>
              <InputLabel id="requiredTag-label">{i18n.t("settings.settings.options.requiredTag")}</InputLabel>
              <Select
                labelId="requiredTag-label"
                label={i18n.t("settings.settings.options.requiredTag")}
                value={requiredTag}
                onChange={async (e) => {
                  handleRequiredTag(e.target.value);
                }}
              >
                <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
                <MenuItem value={"enabled"}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
              </Select>
              {loadingRequiredTag && (
                <FormHelperText className={classes.helperUpdating}>
                  {i18n.t("settings.settings.options.updating")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {enableLGPD === "enabled" && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <InputLabel id="lgpdDeleteMessage-label">{i18n.t("settings.settings.LGPD.obfuscateMessageDelete")}</InputLabel>
                  <Select
                    labelId="lgpdDeleteMessage-label"
                    label={i18n.t("settings.settings.LGPD.obfuscateMessageDelete")}
                    value={lgpdDeleteMessage}
                    onChange={async (e) => {
                      handleLGPDDeleteMessage(e.target.value);
                    }}
                  >
                    <MenuItem value={"disabled"}>{i18n.t("settings.settings.LGPD.disabled")}</MenuItem>
                    <MenuItem value={"enabled"}>{i18n.t("settings.settings.LGPD.enabled")}</MenuItem>
                  </Select>
                  {loadingLGPDDeleteMessage && (
                    <FormHelperText className={classes.helperUpdating}>
                      {i18n.t("settings.settings.options.updating")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <InputLabel id="lgpdConsent-label">
                    {i18n.t("settings.settings.LGPD.alwaysConsent")}
                  </InputLabel>
                  <Select
                    labelId="lgpdConsent-label"
                    label={i18n.t("settings.settings.LGPD.alwaysConsent")}
                    value={lgpdConsent}
                    onChange={async (e) => {
                      handleLGPDConsent(e.target.value);
                    }}
                  >
                    <MenuItem value={"disabled"}>{i18n.t("settings.settings.LGPD.disabled")}</MenuItem>
                    <MenuItem value={"enabled"}>{i18n.t("settings.settings.LGPD.enabled")}</MenuItem>
                  </Select>
                  {loadingLGPDConsent && (
                    <FormHelperText className={classes.helperUpdating}>
                      {i18n.t("settings.settings.options.updating")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <InputLabel id="lgpdHideNumber-label">
                    {i18n.t("settings.settings.LGPD.obfuscatePhoneUser")}
                  </InputLabel>
                  <Select
                    labelId="lgpdHideNumber-label"
                    label={i18n.t("settings.settings.LGPD.obfuscatePhoneUser")}
                    value={lgpdHideNumber}
                    onChange={async (e) => {
                      handleLGPDHideNumber(e.target.value);
                    }}
                  >
                    <MenuItem value={"disabled"}>{i18n.t("settings.settings.LGPD.disabled")}</MenuItem>
                    <MenuItem value={"enabled"}>{i18n.t("settings.settings.LGPD.enabled")}</MenuItem>
                  </Select>
                  {loadingLGPDHideNumber && (
                    <FormHelperText className={classes.helperUpdating}>
                      {i18n.t("settings.settings.options.updating")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <FormControl className={classes.selectContainer}>
                  <TextField
                    id="lgpdMessage"
                    name="lgpdMessage"
                    size="small"
                    multiline
                    rows={3}
                    label={i18n.t("settings.settings.LGPD.welcome")}
                    variant="outlined"
                    value={lgpdMessage}
                    onChange={async (e) => {
                      handleLGPDMessage(e.target.value);
                    }}
                    helperText={loadinglgpdMessage && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <FormControl className={classes.selectContainer}>
                  <TextField
                    id="lgpdLink"
                    name="lgpdLink"
                    size="small"
                    label={i18n.t("settings.settings.LGPD.linkLGPD")}
                    variant="outlined"
                    value={lgpdLink}
                    onChange={async (e) => {
                      handleLGPDLink(e.target.value);
                    }}
                    helperText={loadingLGPDLink && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* SECCIÓN 5: PASARELAS DE PAGO Y FACTURACIÓN */}
      {isSuper() && (
        <Paper className={classes.sectionCard} elevation={0}>
          <Typography className={classes.sectionHeader}>
            <PaymentIcon />
            {i18n.t("settings.settings.sections.paymentGateways") || "Pasarelas de Pago y Facturación"}
          </Typography>

          <Tabs
            value={paymentTab}
            onChange={(e, val) => setPaymentTab(val)}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            className={classes.paymentTabs}
          >
            <Tab value="efi" label={i18n.t("settings.settings.options.paymentGatewayEfi")} />
            <Tab value="mp" label={i18n.t("settings.settings.options.paymentGatewayMp")} />
            <Tab value="stripe" label={i18n.t("settings.settings.options.paymentGatewayStripe")} />
            <Tab value="asaas" label={i18n.t("settings.settings.options.paymentGatewayAsaas")} />
          </Tabs>

          {paymentTab === "efi" && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="eficlientid"
                    name="eficlientid"
                    size="small"
                    label={i18n.t("settings.settings.options.clientId")}
                    variant="outlined"
                    value={eficlientidType}
                    onChange={async (e) => {
                      handleChangeEfiClientid(e.target.value);
                    }}
                    helperText={loadingEfiClientidType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="eficlientsecret"
                    name="eficlientsecret"
                    size="small"
                    label={i18n.t("settings.settings.options.clientSecret")}
                    variant="outlined"
                    value={eficlientsecretType}
                    onChange={async (e) => {
                      handleChangeEfiClientsecret(e.target.value);
                    }}
                    helperText={loadingEfiClientsecretType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="efichavepix"
                    name="efichavepix"
                    size="small"
                    label={i18n.t("settings.settings.options.chavePix")}
                    variant="outlined"
                    value={efichavepixType}
                    onChange={async (e) => {
                      handleChangeEfiChavepix(e.target.value);
                    }}
                    helperText={loadingEfiChavepixType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}

          {paymentTab === "mp" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="mpaccesstoken"
                    name="mpaccesstoken"
                    size="small"
                    label={i18n.t("settings.settings.options.accessToken")}
                    variant="outlined"
                    value={mpaccesstokenType}
                    onChange={async (e) => {
                      handleChangempaccesstoken(e.target.value);
                    }}
                    helperText={loadingmpaccesstokenType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}

          {paymentTab === "stripe" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="stripeprivatekey"
                    name="stripeprivatekey"
                    size="small"
                    label={i18n.t("settings.settings.options.stripePrivateKey")}
                    variant="outlined"
                    value={stripeprivatekeyType}
                    onChange={async (e) => {
                      handleChangestripeprivatekey(e.target.value);
                    }}
                    helperText={loadingstripeprivatekeyType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}

          {paymentTab === "asaas" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl variant="outlined" size="small" className={classes.selectContainer}>
                  <TextField
                    id="asaastoken"
                    name="asaastoken"
                    size="small"
                    label={i18n.t("settings.settings.options.tokenAsaas")}
                    variant="outlined"
                    value={asaastokenType}
                    onChange={async (e) => {
                      handleChangeasaastoken(e.target.value);
                    }}
                    helperText={loadingasaastokenType && i18n.t("settings.settings.options.updating")}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}
    </div>
  );
}


